import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { usePersonNames } from '../usePersonNames';
import type { PersonnelDetail, PersonnelFamilyMember } from '../personnelTypes';

/**
 * Family background — CS Form 212, section II.
 *
 * **The names are not in the rows, and that shapes the whole panel.** Every relative is a
 * subject in the identity store, so a row carries a `personId` and this resolves the ids on
 * screen through one batch call — `usePersonNames`, which is INTEGRATION_REFERENCE §4.2 made
 * into a hook. A name column reading off the row would be the copy the split exists to prevent.
 *
 * Two consequences a reader of this file should expect:
 *
 * - **Search does not match names.** It matches the occupation, the employer and the relation —
 *   the values this system actually holds. Searching a name would mean resolving every row
 *   before filtering, which is the thing the batch cap exists to stop.
 * - **Sort does not order by name either**, for the same reason. The default is the form's own
 *   order, which is the relation catalog's `sortOrder`: spouse, father, mother, then children.
 *
 * **The NAME is the anchor column even so.** Product call, 14 Sep 2026 — the relation used to
 * lead, so the list view titled every row "Spouse" / "Father" and buried whose record it was in
 * the summary line. A row's title is its identity, and the identity of a family member is the
 * person, not their relationship to the employee. The relation moved into the summary line,
 * directly after the provenance, which is where the list view puts the next most important
 * value. That the anchor can be neither sorted nor searched is unusual and deliberate — the
 * personnel list's own anchor column has exactly the same shape and for the same reason.
 */
export function FamilyPanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (member: PersonnelFamilyMember) => void;
  onDelete: (member: PersonnelFamilyMember) => void;
  onBulkDelete: (members: PersonnelFamilyMember[]) => void;
  onRestore: (member: PersonnelFamilyMember) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const members = useMemo(
    () => (showDeleted ? record.familyMembers : record.familyMembers.filter((m) => m.isActive)),
    [record.familyMembers, showDeleted],
  );

  /*
    ONE resolve for every row the panel holds, not one per page of the table.

    The whole list arrives with the record and the table pages it in memory, so resolving the
    page would re-hit the endpoint on every page change for ids that are already to hand. A
    family is a handful of people, comfortably inside the batch cap.
  */
  const personIds = useMemo(() => members.map((m) => m.personId), [members]);
  const names = usePersonNames(personIds);

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      // Deliberately NOT the name — see the note on the component.
      const matched = term
        ? members.filter((m) =>
            [m.occupation, m.employerName, m.businessAddress, m.relationName]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : members;

      const field = query.sort?.replace(/^-/, '') ?? 'relation';
      const descending = query.sort?.startsWith('-') ?? false;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'relation') {
          /*
            The CATALOG's order, which is the form's — spouse, father, mother, then children.
            Sorting on `relationName` would put "Child" above "Father", which is not how
            section II reads, and it is why the server sends the sort order down with each row.
          */
          const byRelation =
            a.relationSortOrder - b.relationSortOrder || a.createdAt.localeCompare(b.createdAt);
          return byRelation * (descending ? -1 : 1);
        }

        const pick = (row: PersonnelFamilyMember) =>
          field === 'occupation' ? (row.occupation ?? '') : (row.employerName ?? '');

        return String(pick(a)).localeCompare(String(pick(b))) * (descending ? -1 : 1);
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [members],
  );

  const state = useDataTable<PersonnelFamilyMember>({
    fetchPage,
    pageSize: 10,
    defaultSort: 'relation',
  });

  const columns: DataColumn<PersonnelFamilyMember>[] = [
    {
      key: 'name',
      header: 'Name',
      width: 260,
      /*
        THE ANCHOR, and neither sortable nor searchable — the page must not offer either. The
        value lives in another database; sorting it would mean resolving the whole table before
        paging it, and searching it would mean copying names into `rsys_`.

        The three absences are distinguished rather than collapsed to a blank: a resolve still
        in flight, a resolve that failed, and a person the store has erased are different facts,
        and rendering all three the same turns two of them into what looks like a bug.
      */
      render: (m) => {
        const status = names.statusOf(m.personId);

        if (names.failed) return <TextCell value="Unavailable" />;
        if (status === 'erased') return <TextCell value="Erased" />;

        return <TextCell value={names.nameOf(m.personId) ?? (names.loading ? '…' : 'Unknown')} />;
      },
    },
    {
      key: 'relation',
      header: 'Relation',
      width: 130,
      sortable: true,
      // FIRST in the summary line, so a list row reads "Created by … · 14 Sep 2026 · Spouse".
      summary: true,
      // The name off the catalog row, resolved live — renaming a relation corrects every record.
      render: (m) => <TextCell value={m.relationName} />,
    },
    {
      key: 'occupation',
      header: 'Occupation',
      width: 180,
      sortable: true,
      summary: true,
      // Asked only where the relation says so, so a blank is a question that was never put.
      render: (m) => <TextCell value={m.occupation} />,
    },
    {
      key: 'employerName',
      header: 'Employer',
      width: 200,
      sortable: true,
      summary: true,
      render: (m) => <TextCell value={m.employerName} />,
    },
    {
      key: 'businessAddress',
      header: 'Business address',
      width: 220,
      render: (m) => <TextCell value={m.businessAddress} />,
    },
    {
      key: 'telephoneNumber',
      header: 'Telephone',
      width: 150,
      render: (m) => <TextCell value={m.telephoneNumber} mono />,
    },
    {
      /*
        The record's state, and `status` is what puts it on the RIGHT of a list row rather than
        in the summary line — the outermost position, exactly where an index list view puts it.
        The panel moves it to the end of the table's columns too, so the two views agree.

        Without it the list gave a deleted member no marker at all while "Show deleted" was on,
        which is the one state a row must never be silent about.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (m) =>
        m.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelFamilyMember>
        id="personnel-family"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records;
          it is a poor way to read ONE, because the columns are spread across a scroll and the
          narrow ones truncate. The preview is built from these same `columns`, so it cannot
          drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Family member', size: '2xl' }}
        columns={columns}
        rowKey={(m) => m.id}
        // Says what it searches, because it is NOT what a reader would assume. The names are in
        // another system and this panel cannot filter on them.
        searchPlaceholder="Search occupation or employer"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The kebab's entries. The shell owns the gutter, the menu and the right-click
          shortcut — this is only what goes IN the menu, and the same array feeds the table
          and the list, so the two cannot offer different actions on one row.

          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not
          something the reader is part-way through, it is simply not an action here.
        */
        rowActions={(m) => [
          {
            key: `edit-${m.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: m.isActive
              ? null
              : 'This family member is deleted. Restore them before editing.',
            onClick: () => onEdit(m),
          },
          m.isActive
            ? {
                key: `delete-${m.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(m),
              }
            : {
                key: `restore-${m.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(m),
              },
        ]}
        otherInfo={(m) => ({
          createdAt: m.createdAt,
          createdByName: m.createdByName,
          updatedAt: m.updatedAt,
          updatedByName: m.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default. It carried `selectable={false}`,
          and that reading is the one CLAUDE.md names: the opt-out is only for a table nothing
          can ever be done to, never for one whose bulk action is merely unbuilt. Every row
          here can be deleted one at a time, so the column belongs — and the bulk action ships
          with it rather than leaving a selection with nowhere to go.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((m) => m.isActive);

          return [
            {
              key: 'delete-family-members',
              label: `Delete ${deletable.length} family member${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected family member is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? (
              <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
            ) : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No family background recorded yet."
        emptySearchMessage="No family member matches that search."
      />
    </ProfileSection>
  );
}
